<template>
    <li v-if="item" :class="{ 'layout-root-menuitem': root, 'active-menuitem': isActiveMenu }">
        <div v-if="root && !item.disable" class="layout-menuitem-root-text">
            {{ item.label }}
        </div>
        <div v-if="item.label == 'divider'" class="h-[1px] w-full bg-white/20" />
        <a v-if="item.items && !item.disable" tabindex="0" @click="itemClick($event, item)">
            <i :class="item.icon" class="layout-menuitem-icon" />
            <span class="layout-menuitem-text">{{ item.label }}</span>
            <i v-if="item.items" class="pi pi-fw pi-angle-down layout-submenu-toggler" />
        </a>
        <router-link
            v-if="item.to && !item.items && !item.disable"
            :class="{ 'active-route': checkActiveRoute(item) }"
            tabindex="0"
            :to="{ name: item.to }"
            @click="itemClick($event, item)"
        >
            <i :class="item.icon" class="layout-menuitem-icon" />
            <span class="layout-menuitem-text">{{ item.label }}</span>
            <i v-if="item.items" class="pi pi-fw pi-angle-down layout-submenu-toggler" />
        </router-link>
        <Transition v-if="item.items && !item.disable" name="layout-submenu">
            <ul v-show="root ? true : isActiveMenu" class="layout-submenu">
                <app-menu-item
                    v-for="(child, i) in item.items"
                    :key="`child_${child.label}_${i}`"
                    :index="i"
                    :item="child"
                    :parentItemKey="itemKey"
                    :root="false"
                />
            </ul>
        </Transition>
    </li>
</template>

<script setup lang="ts">
    import { type PropType, type Ref, onBeforeMount, ref, watch } from 'vue'
    import { useRoute } from 'vue-router'

    import { useLayout } from '../layout'

    import type { NavLink } from '@/types/Navigation'

    const props = defineProps({
        item: {
            type: Object as PropType<NavLink>,
            default: undefined
        },
        index: {
            type: Number,
            default: 0
        },
        root: {
            type: Boolean,
            default: true
        },
        parentItemKey: {
            type: String,
            default: null
        }
    })

    // data
    const route = useRoute()
    const { layoutConfig, layoutState, setActiveMenuItem, onMenuToggle } = useLayout()
    const isActiveMenu: Ref<boolean> = ref(false)
    const itemKey: Ref<string> = ref('')

    // methods
    const itemClick = (event: Event, item: NavLink) => {
        if (item.disable) {
            event.preventDefault()
            return
        }

        const { staticMenuMobileActive } = layoutState

        if (item.to && staticMenuMobileActive.value) {
            onMenuToggle()
        }

        let foundItemKey = itemKey.value
        if (item.items && item.items.length > 0 && isActiveMenu.value) foundItemKey = props.parentItemKey

        setActiveMenuItem(foundItemKey)
    }

    const checkActiveRoute = (item: NavLink) => {
        return route.name === item.to
    }

    // hook
    onBeforeMount(() => {
        itemKey.value = props.parentItemKey ? `${props.parentItemKey}-${props.index.toString()}` : String(props.index)

        if (props.parentItemKey && props.parentItemKey.length > 0 && props.item && checkActiveRoute(props.item)) {
            setActiveMenuItem(props.parentItemKey)
        }

        const activeItem = layoutState.activeMenuItem.value
        if (activeItem !== '') isActiveMenu.value = activeItem === itemKey.value || activeItem ? activeItem.startsWith(`${itemKey.value}-`) : false
    })

    // watcher
    watch(() => layoutConfig.activeMenuItem.value, (newVal: string) => {
        isActiveMenu.value = newVal === itemKey.value || newVal.startsWith(`${itemKey.value}-`)
    })
</script>
